// Transitions PR-1 — the cross-loan Transitions console at
// /portfolio-admin/transitions (the TpsOversightPage / StatementQueuePage
// shape): every open transition with its status and tender countdown,
// soonest tender first. The BMSP covenant-countdown queue joins in PR-2.

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  EmptyState,
  PageHeader,
  Skeleton,
  StatusBadge,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  type StatusTone,
} from "@/design-system/components/ui";
import {
  TRANSITION_STATUS_LABEL,
  TRANSITION_TYPE_INFO,
  type BmspQueueRowDto,
  type LoanTransitionStatus,
  type TransitionConsoleRowDto,
} from "@/api/portfolioAdmin/loanTransitionsApi";
import {
  useBmspQueue,
  useTransitionsConsole,
} from "@/hooks/portfolioAdmin/useLoanTransitions";

const STATUS_TONE: Record<LoanTransitionStatus, StatusTone> = {
  Draft: "neutral",
  Initiated: "progress",
  AwaitingLinkedDeal: "info",
  PendingApproval: "warn",
  Scheduled: "onyx",
  Completed: "ok",
  Canceled: "muted",
  Lapsed: "danger",
};

function fmtDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function signedDaysBadge(days: number | null): ReactNode {
  if (days == null) return <span className="text-gray-mid">—</span>;
  const tone: StatusTone =
    days < 0 ? "muted" : days <= 30 ? "danger" : days <= 90 ? "warn" : "info";
  return (
    <StatusBadge tone={tone}>
      {days < 0 ? `${-days}d ago` : days === 0 ? "today" : `${days}d`}
    </StatusBadge>
  );
}

function bmspStatus(row: BmspQueueRowDto): ReactNode {
  if (row.countdown.hasOpenBmspTransition)
    return <StatusBadge tone="progress">BMSP transition open</StatusBadge>;
  if (row.countdown.windowMissed)
    return <StatusBadge tone="danger">Month-73 window missed</StatusBadge>;
  return <StatusBadge tone="neutral">On track</StatusBadge>;
}

function tenderCell(row: TransitionConsoleRowDto) {
  if (row.tenderDate == null || row.daysToTender == null)
    return <span className="text-gray-mid">—</span>;
  const days = row.daysToTender;
  const tone: StatusTone = days <= 7 ? "danger" : days <= 30 ? "warn" : "info";
  return (
    <StatusBadge tone={tone}>
      {days < 0
        ? `${-days}d overdue`
        : days === 0
          ? "due today"
          : `${days}d`}{" "}
      · {fmtDate(row.tenderDate)}
    </StatusBadge>
  );
}

export default function TransitionsConsolePage() {
  const query = useTransitionsConsole();
  const rows = query.data ?? [];
  const bmsp = useBmspQueue();
  const bmspRows = bmsp.data ?? [];

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Finance"
        title="Transitions"
        description="Every open loan exit path — payoffs awaiting tender, assumptions awaiting deals, extensions in approval."
      />

      {query.isLoading ? (
        <Card>
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-2/3 mt-space-2" />
        </Card>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No open transitions"
          description="Start one from a loan's Transitions tab."
        />
      ) : (
        <Card>
          <Table data-testid="transitions-console-table">
            <THead>
              <TR>
                <TH>Loan</TH>
                <TH>Exit path</TH>
                <TH>Status</TH>
                <TH>Tender</TH>
                <TH>Initiated</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((row) => (
                <TR key={row.id}>
                  <TD>
                    <Link
                      to={`/portfolio-admin/loans/${row.loanId}?tab=transitions`}
                      className="text-gold-shadow hover:underline"
                    >
                      {row.loanNumber}
                    </Link>
                  </TD>
                  <TD>{TRANSITION_TYPE_INFO[row.type].label}</TD>
                  <TD>
                    <StatusBadge tone={STATUS_TONE[row.status]}>
                      {TRANSITION_STATUS_LABEL[row.status]}
                    </StatusBadge>
                  </TD>
                  <TD>{tenderCell(row)}</TD>
                  <TD>{fmtDate(row.initiatedAt)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      {/* Transitions PR-2 — the BMSP covenant-countdown queue: every live
          balloon loan's Month-73 deadline + balloon maturity, soonest first. */}
      <Card>
        <h3 className="font-display font-bold text-h3 m-0 mb-space-1">
          BMSP countdown
        </h3>
        <p className="text-small text-gray-mid m-0 mb-space-3">
          Balloon loans by proximity to the Month-73 application deadline (a
          loan-agreement covenant) and balloon maturity.
        </p>
        {bmsp.isLoading ? (
          <Skeleton className="h-5 w-full" />
        ) : bmspRows.length === 0 ? (
          <p className="text-small text-gray-mid m-0">No live balloon loans.</p>
        ) : (
          <Table data-testid="bmsp-queue-table">
            <THead>
              <TR>
                <TH>Loan</TH>
                <TH>Month</TH>
                <TH>Month-73 deadline</TH>
                <TH>Balloon maturity</TH>
                <TH>Status</TH>
              </TR>
            </THead>
            <TBody>
              {bmspRows.map((row) => (
                <TR key={row.loanId}>
                  <TD>
                    <Link
                      to={`/portfolio-admin/loans/${row.loanId}?tab=transitions`}
                      className="text-gold-shadow hover:underline"
                    >
                      {row.loanNumber}
                    </Link>
                  </TD>
                  <TD>
                    {row.countdown.currentMonthNumber} / {row.countdown.termMonths}
                  </TD>
                  <TD>
                    {row.countdown.hasBmspWindow ? (
                      <>
                        {signedDaysBadge(row.countdown.daysToDeadline)}{" "}
                        <span className="text-gray-mid">
                          {fmtDate(row.countdown.applicationDeadline)}
                        </span>
                      </>
                    ) : (
                      <span className="text-gray-mid">n/a (short term)</span>
                    )}
                  </TD>
                  <TD>
                    {signedDaysBadge(row.countdown.daysToMaturity)}{" "}
                    <span className="text-gray-mid">
                      {fmtDate(row.countdown.balloonMaturity)}
                    </span>
                  </TD>
                  <TD>{bmspStatus(row)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
